import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Activity, Radio, AlertOctagon, RefreshCw, Search, Filter, Gauge, Zap } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import SystemStatusPanel from '@/components/SystemStatusPanel';

// The 10-category taxonomy — matches backend `_CATEGORY_RULES` order.
const CATEGORY_ORDER = ["access", "guests", "bookings", "content", "integrations",
                        "platform", "system", "performance", "errors", "security"];

// ---------- helpers ----------
const LEVEL_STYLE = {
  info:  "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  warn:  "text-amber-600  bg-amber-500/10  border-amber-500/30",
  error: "text-red-600    bg-red-500/10    border-red-500/30",
};
const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const fmtLocal = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour12: false });
  } catch { return iso; }
};
const toIsoOrEmpty = (localVal) => {
  // <input type="datetime-local"> → ISO
  if (!localVal) return "";
  try { return new Date(localVal).toISOString(); } catch { return ""; }
};

// ---------- Metrics ----------
function MetricsPanel({ liveTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/platform/metrics"); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Metrics failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (liveTick) load(); }, [liveTick, load]);
  if (loading && !data) return <div className="text-sm text-muted-foreground">Loading metrics…</div>;

  const Stat = ({ label, value, tone = "default" }) => (
    <div className={`border p-3 ${tone === "warn" ? "border-amber-500/40 bg-amber-500/5" : tone === "bad" ? "border-red-500/40 bg-red-500/5" : "border-border"}`}>
      <div className="overline text-primary/50 text-[10px] mb-1">{label}</div>
      <div className="font-display text-lg truncate">{value}</div>
    </div>
  );
  const p95Tone = data?.p95_ms > 2500 ? "bad" : data?.p95_ms > 1000 ? "warn" : "default";
  return (
    <section className="border border-border bg-card" data-testid="metrics-panel">
      <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent flex items-center gap-2"><Gauge className="h-3 w-3" /> Performance metrics</div>
          <h3 className="font-display text-lg mt-1">Request latency & error rates</h3>
          <p className="text-xs text-muted-foreground mt-1">Rolling window of the last {data?.sample_size || 0} requests. Auto-refresh every 10 s.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="metrics-refresh"><RefreshCw className="h-3 w-3 mr-1.5" /> Refresh</Button>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <Stat label="Requests" value={data?.requests_total ?? "—"} />
          <Stat label="5xx errors" value={data?.requests_5xx ?? 0} tone={data?.requests_5xx > 0 ? "bad" : "default"} />
          <Stat label="Avg" value={`${data?.avg_ms ?? 0} ms`} />
          <Stat label="P50" value={`${data?.p50_ms ?? 0} ms`} />
          <Stat label="P95" value={`${data?.p95_ms ?? 0} ms`} tone={p95Tone} />
          <Stat label="P99" value={`${data?.p99_ms ?? 0} ms`} />
          <Stat label="Max" value={`${data?.max_ms ?? 0} ms`} />
        </div>

        {[
          { title: "Slowest routes",   rows: data?.top_slow || [],    testid: "top-slow" },
          { title: "Highest traffic",  rows: data?.top_traffic || [], testid: "top-traffic" },
          { title: "Errors by route",  rows: data?.top_errors || [],  testid: "top-errors" },
        ].map((sec) => (
          <div key={sec.title}>
            <div className="overline text-primary/50 mb-2 text-[11px]">{sec.title}</div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="text-left"><th className="p-2">Route</th><th className="p-2 text-right">Count</th><th className="p-2 text-right">Errors</th><th className="p-2 text-right">Avg</th><th className="p-2 text-right">Max</th></tr>
                </thead>
                <tbody data-testid={sec.testid}>
                  {sec.rows.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">—</td></tr>}
                  {sec.rows.map((r) => (
                    <tr key={r.route} className="border-t border-border">
                      <td className="p-2 font-mono break-all">{r.route}</td>
                      <td className="p-2 text-right">{r.count}</td>
                      <td className={`p-2 text-right ${r.err > 0 ? "text-red-600 font-semibold" : ""}`}>{r.err}</td>
                      <td className="p-2 text-right">{r.avg_ms} ms</td>
                      <td className={`p-2 text-right ${r.max_ms > 2000 ? "text-amber-600" : ""}`}>{r.max_ms} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Event log ----------
const EVENT_TYPE_OPTIONS = [
  { v: "",              label: "All types" },
  { v: "login.",        label: "login.*" },
  { v: "mqtt.",         label: "mqtt.*" },
  { v: "email.",        label: "email.*" },
  { v: "http.",         label: "http.*" },
  { v: "app.exception", label: "app.exception" },
];

function EventLogPanel({ liveTick }) {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [auto, setAuto] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform/events", {
        params: { limit: 300, level, category, search,
                  since: toIsoOrEmpty(since), until: toIsoOrEmpty(until) },
      });
      setItems(data.items || []);
      setCounters(data.counters || null);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, [level, category, search, since, until]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!auto) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [auto, load]);
  // Refresh instantly whenever a live event arrives over the WS.
  useEffect(() => { if (liveTick) load(); }, [liveTick, load]);

  return (
    <section className="border border-border bg-card" data-testid="event-log-panel">
      <div className="p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-accent flex items-center gap-2"><Zap className="h-3 w-3" /> App event log</div>
            <h3 className="font-display text-lg mt-1">Login, crashes, integrations, exceptions</h3>
            <p className="text-xs text-muted-foreground mt-1">Live via WebSocket · in-memory ring buffer (last 2 000 events).</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={auto} onCheckedChange={setAuto} data-testid="ev-autorefresh" /> Auto-refresh
            </label>
            <Button variant="outline" size="sm" onClick={load} data-testid="ev-refresh"><RefreshCw className="h-3 w-3 mr-1.5" /> Refresh</Button>
          </div>
        </div>
        {counters && (
          <>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
              <span className="px-2 py-0.5 border border-border">Total {counters.events.total}</span>
              <span className={`px-2 py-0.5 border ${LEVEL_STYLE.info}`}>info {counters.events.info}</span>
              <span className={`px-2 py-0.5 border ${LEVEL_STYLE.warn}`}>warn {counters.events.warn}</span>
              <span className={`px-2 py-0.5 border ${LEVEL_STYLE.error}`}>error {counters.events.error}</span>
            </div>
            {counters.events.by_category && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ev-category-chips">
                {CATEGORY_ORDER.filter((c) => (counters.events.by_category[c] || 0) > 0).map((c) => {
                  const active = category === c;
                  return (
                    <button key={c} onClick={() => setCategory(active ? "" : c)}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${active ? "border-accent bg-accent/20 text-accent" : "border-border hover:border-accent/60"}`}
                            data-testid={`ev-chip-${c}`}>
                      {c} · {counters.events.by_category[c]}
                    </button>
                  );
                })}
                {category && (
                  <button onClick={() => setCategory("")}
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-destructive/40 text-destructive"
                          data-testid="ev-chip-clear">clear filter</button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-border grid grid-cols-2 md:grid-cols-6 gap-2 bg-muted/20">
        <Select value={level || "__any"} onValueChange={(v) => setLevel(v === "__any" ? "" : v)}>
          <SelectTrigger data-testid="ev-level" className="text-xs h-9"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any">Any level</SelectItem>
            <SelectItem value="info">info</SelectItem>
            <SelectItem value="warn">warn</SelectItem>
            <SelectItem value="error">error</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category || "__any"} onValueChange={(v) => setCategory(v === "__any" ? "" : v)}>
          <SelectTrigger data-testid="ev-category" className="text-xs h-9"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any">All categories</SelectItem>
            {CATEGORY_ORDER.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="col-span-2 relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search message/type…" value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-8 h-9 text-xs" data-testid="ev-search" />
        </div>
        <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)}
               className="h-9 text-xs" data-testid="ev-since" title="Since" />
        <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)}
               className="h-9 text-xs" data-testid="ev-until" title="Until" />
      </div>

      <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left">
              <th className="p-2 w-40">Time (UTC)</th><th className="p-2 w-16">Level</th><th className="p-2 w-40">Type</th><th className="p-2">Message</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                {loading ? "Loading…" : "No events match your filters."}
              </td></tr>
            )}
            {items.map((e) => {
              const open = expandedEvent === e.id;
              const meta = e.meta || {};
              const hasMeta = Object.keys(meta).length > 0;
              return (
                <React.Fragment key={e.id}>
                  <tr className={`border-t border-border hover:bg-muted/30 ${hasMeta ? "cursor-pointer" : ""}`}
                      onClick={() => hasMeta && setExpandedEvent(open ? null : e.id)}
                      data-testid={`ev-row-${e.type}`}>
                    <td className="p-2 font-mono whitespace-nowrap text-muted-foreground" title={e.ts}>
                      <div>{fmtLocal(e.ts)}</div>
                      <div className="text-[10px] opacity-60">{e.ts.replace('T', ' ').slice(0, 19)} UTC</div>
                    </td>
                    <td className="p-2"><span className={`px-1.5 py-0.5 border text-[10px] uppercase ${LEVEL_STYLE[e.level] || ""}`}>{e.level}</span></td>
                    <td className="p-2 font-mono text-accent">{e.type}</td>
                    <td className="p-2 break-all">{e.message}</td>
                  </tr>
                  {open && hasMeta && (
                    <tr className="bg-muted/10">
                      <td colSpan={4} className="p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-mono" data-testid={`ev-meta-${e.id}`}>
                          {Object.entries(meta).map(([k, v]) => (
                            <div key={k} className="flex gap-2 border border-border p-2 bg-background">
                              <span className="text-primary/60 min-w-[110px]">{k}</span>
                              <span className="break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------- MQTT sniffer ----------
function MqttSnifferPanel({ liveTick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [auto, setAuto] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform/mqtt/messages", {
        params: { limit: 200, topic, since: toIsoOrEmpty(since), until: toIsoOrEmpty(until),
                  only_failed: onlyFailed },
      });
      setItems(data.items || []);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, [topic, since, until, onlyFailed]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!auto) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [auto, load]);
  useEffect(() => { if (liveTick) load(); }, [liveTick, load]);

  const topics = useMemo(() => Array.from(new Set(items.map((m) => m.topic))).slice(0, 12), [items]);

  return (
    <section className="border border-border bg-card" data-testid="mqtt-sniffer-panel">
      <div className="p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-accent flex items-center gap-2"><Radio className="h-3 w-3" /> Live MQTT sniffer</div>
            <h3 className="font-display text-lg mt-1">Every message the platform tried to publish</h3>
            <p className="text-xs text-muted-foreground mt-1">Ring buffer of the last 500 messages. Click a row to see the full payload.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={auto} onCheckedChange={setAuto} data-testid="mq-autorefresh" /> Auto-refresh
            </label>
            <Button variant="outline" size="sm" onClick={load} data-testid="mq-refresh"><RefreshCw className="h-3 w-3 mr-1.5" /> Refresh</Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-border grid grid-cols-2 md:grid-cols-6 gap-2 bg-muted/20 items-center">
        <div className="col-span-2 relative">
          <Filter className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Topic contains… (e.g. booking/created)" value={topic}
                 onChange={(e) => setTopic(e.target.value)} className="pl-8 h-9 text-xs" data-testid="mq-topic" />
        </div>
        <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)}
               className="h-9 text-xs" data-testid="mq-since" title="Since" />
        <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)}
               className="h-9 text-xs" data-testid="mq-until" title="Until" />
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={onlyFailed} onCheckedChange={setOnlyFailed} data-testid="mq-only-failed" /> Failed only
        </label>
        <div className="text-[11px] text-muted-foreground text-right">Showing {items.length}</div>
      </div>

      {/* Topic chip shortcuts */}
      {topics.length > 0 && (
        <div className="px-4 py-2 border-b border-border flex flex-wrap gap-1.5 bg-muted/10">
          <span className="text-[10px] uppercase tracking-wider text-primary/50 self-center">Quick filter:</span>
          {topics.map((t) => (
            <button key={t} onClick={() => setTopic(t)}
                    className="text-[10px] font-mono px-2 py-0.5 border border-border hover:bg-accent/10 hover:border-accent"
                    data-testid={`mq-chip-${t.replace(/[^a-z0-9]/gi, '-')}`}>
              {t}
            </button>
          ))}
          {topic && (
            <button onClick={() => setTopic("")}
                    className="text-[10px] px-2 py-0.5 border border-destructive/40 text-destructive hover:bg-destructive/10"
                    data-testid="mq-clear-topic">clear</button>
          )}
        </div>
      )}

      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left">
              <th className="p-2 w-40">Time</th><th className="p-2">Topic</th><th className="p-2 w-16">Result</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">
                {loading ? "Loading…" : "No MQTT messages yet."}
              </td></tr>
            )}
            {items.map((m, i) => {
              const key = `${m.ts}-${i}`;
              const open = expanded === key;
              return (
                <React.Fragment key={key}>
                  <tr className="border-t border-border hover:bg-muted/30 cursor-pointer"
                      onClick={() => setExpanded(open ? null : key)}
                      data-testid={`mq-row-${i}`}>
                    <td className="p-2 font-mono whitespace-nowrap text-muted-foreground">{fmtTs(m.ts)}</td>
                    <td className="p-2 font-mono text-accent break-all">{m.topic}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 border text-[10px] uppercase ${m.ok ? LEVEL_STYLE.info : LEVEL_STYLE.error}`}>
                        {m.ok ? "ok" : "fail"}
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-muted/10">
                      <td colSpan={3} className="p-3">
                        {!m.ok && m.error && (
                          <div className="mb-2 text-[11px] text-red-600 font-mono break-all">{m.error}</div>
                        )}
                        <pre className="text-[11px] font-mono bg-background border border-border p-3 overflow-x-auto max-h-64" data-testid={`mq-payload-${i}`}>
                          {JSON.stringify(m.payload, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------- Root ----------
export default function DiagnosticsPanel() {
  // A single ticker bumped whenever ANY WS event arrives — child panels re-fetch.
  const [liveTick, setLiveTick] = useState(0);
  useLiveEvents(() => setLiveTick((n) => n + 1));

  return (
    <div className="space-y-6" data-testid="diagnostics-panel">
      <div className="border border-accent/30 bg-accent/5 p-4 flex items-start gap-3">
        <AlertOctagon className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
        <div>
          <div className="overline text-accent text-[11px]">Diagnostics · Developer only</div>
          <p className="text-xs text-muted-foreground mt-1">
            Live troubleshooting console — request latencies, structured event log with 10-category filters,
            and an MQTT wire-sniffer showing exactly what was published to your broker. All panels refresh
            in real-time over WebSocket. Data lives in memory and resets on backend restart.
          </p>
        </div>
      </div>
      <SystemStatusPanel />
      <MetricsPanel liveTick={liveTick} />
      <EventLogPanel liveTick={liveTick} />
      <MqttSnifferPanel liveTick={liveTick} />
    </div>
  );
}
